import {
  PIPE_TYPES,
  GRID_SIZE,
  PIPE_CONNECTIONS,
  DIRECTIONS,
  ROTATIONS, // if used elsewhere
} from "../constants/pipes";

// ----------------------
// Helper functions

// Get a random pipe type (from basic types)
export const getRandomPipeType = () => {
  const basicTypes = [
    PIPE_TYPES.STRAIGHT_HORIZONTAL,
    PIPE_TYPES.STRAIGHT_VERTICAL,
    PIPE_TYPES.ELBOW_BOTTOM_LEFT,
    PIPE_TYPES.ELBOW_BOTTOM_RIGHT,
    PIPE_TYPES.ELBOW_LEFT_TOP,
    PIPE_TYPES.ELBOW_TOP_RIGHT,
  ];
  return basicTypes[Math.floor(Math.random() * basicTypes.length)];
};

// Get a random rotation in multiples of 90°
export const getRandomRotation = () => {
  return Math.floor(Math.random() * 4) * 90;
};

// Get a random item from an array
const getRandomItem = (array) => {
  return array[Math.floor(Math.random() * array.length)];
};

// Get all available pipe types for path generation
const getAllPipeTypes = () => [
  PIPE_TYPES.STRAIGHT_HORIZONTAL,
  PIPE_TYPES.STRAIGHT_VERTICAL,
  PIPE_TYPES.ELBOW_BOTTOM_LEFT,
  PIPE_TYPES.ELBOW_BOTTOM_RIGHT,
  PIPE_TYPES.ELBOW_LEFT_TOP,
  PIPE_TYPES.ELBOW_TOP_RIGHT,
  PIPE_TYPES.T_DOWN,
  PIPE_TYPES.T_LEFT,
  PIPE_TYPES.T_RIGHT,
  PIPE_TYPES.T_UP,
  PIPE_TYPES.CROSS,
];

// Get pipe type based on required connections
const getPipeTypeForConnections = (connections) => {
  // Sort connections to match our stored patterns
  const sortedConnections = [...connections].sort();
  const connectionString = sortedConnections.join(",");
  // Find matching pipe type from PIPE_CONNECTIONS
  for (const [type, typeConnections] of Object.entries(PIPE_CONNECTIONS)) {
    if (typeConnections.sort().join(",") === connectionString) {
      return type;
    }
  }
  // Default to straight pipe if no match found
  return PIPE_TYPES.STRAIGHT_HORIZONTAL;
};

// Generate random edge position for starting point.
// Returns an object with x, y, and side (0: top, 1: right, 2: bottom, 3: left)
export const generateRandomEdgePosition = () => {
  const side = Math.floor(Math.random() * 4);
  let x, y;

  switch (side) {
    case 0: // top
      x = Math.floor(Math.random() * GRID_SIZE);
      y = 0;
      break;
    case 1: // right
      x = GRID_SIZE - 1;
      y = Math.floor(Math.random() * GRID_SIZE);
      break;
    case 2: // bottom
      x = Math.floor(Math.random() * GRID_SIZE);
      y = GRID_SIZE - 1;
      break;
    case 3: // left
      x = 0;
      y = Math.floor(Math.random() * GRID_SIZE);
      break;
  }

  return { x, y, side };
};

// Get endpoint pipe type based on edge side.
// For example, a pipe at the top edge should have an endpoint that "points" outward.
export const getEndpointPipeType = (side) => {
  switch (side) {
    case 0:
      return PIPE_TYPES.END_BOTTOM; // top edge requires downward connection
    case 1:
      return PIPE_TYPES.END_LEFT; // right edge requires leftward connection
    case 2:
      return PIPE_TYPES.END_TOP; // bottom edge requires upward connection
    case 3:
      return PIPE_TYPES.END_RIGHT; // left edge requires rightward connection
    default:
      return PIPE_TYPES.END_RIGHT;
  }
};

// Given a starting position and its side, return the opposite edge position.
export const getOppositeEdgePosition = (x, y, side) => {
  switch (side) {
    case 0: // from top, go to bottom
      return { x, y: GRID_SIZE - 1, side: 2 };
    case 1: // from right, go to left
      return { x: 0, y, side: 3 };
    case 2: // from bottom, go to top
      return { x, y: 0, side: 0 };
    case 3: // from left, go to right
      return { x: GRID_SIZE - 1, y, side: 1 };
    default:
      return null;
  }
};

// Check if a position is within the grid bounds.
const isInBounds = (x, y) => {
  return x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE;
};

// Get available directions from a position that haven't been visited.
const getAvailableDirections = (grid, x, y, visited) => {
  const directions = [];
  const possibleDirs = [
    { dx: 0, dy: -1, dir: DIRECTIONS.UP },
    { dx: 1, dy: 0, dir: DIRECTIONS.RIGHT },
    { dx: 0, dy: 1, dir: DIRECTIONS.DOWN },
    { dx: -1, dy: 0, dir: DIRECTIONS.LEFT },
  ];

  for (const { dx, dy, dir } of possibleDirs) {
    const newX = x + dx;
    const newY = y + dy;
    if (isInBounds(newX, newY) && !visited.has(`${newX},${newY}`)) {
      directions.push({ x: newX, y: newY, dir });
    }
  }

  return directions;
};

// Get required connections for a pipe based on incoming and outgoing directions.
const getRequiredConnections = (fromDir, toDir) => {
  const connections = new Set();

  // Add the connection corresponding to the incoming direction (its opposite).
  switch (fromDir) {
    case DIRECTIONS.UP:
      connections.add("bottom");
      break;
    case DIRECTIONS.RIGHT:
      connections.add("left");
      break;
    case DIRECTIONS.DOWN:
      connections.add("top");
      break;
    case DIRECTIONS.LEFT:
      connections.add("right");
      break;
  }

  // Add the connection for the outgoing direction.
  switch (toDir) {
    case DIRECTIONS.UP:
      connections.add("top");
      break;
    case DIRECTIONS.RIGHT:
      connections.add("right");
      break;
    case DIRECTIONS.DOWN:
      connections.add("bottom");
      break;
    case DIRECTIONS.LEFT:
      connections.add("left");
      break;
  }

  return Array.from(connections);
};

// Generate a path (as an array of pipe placements) with optional junctions from start to end.
const generatePath = (start, end) => {
  const path = [];
  const visited = new Set();
  const queue = [
    {
      x: start.x,
      y: start.y,
      path: [],
      fromDir: null,
    },
  ];
  visited.add(`${start.x},${start.y}`);

  while (queue.length > 0) {
    const current = queue.shift();

    // If we reached the end, return the path.
    if (current.x === end.x && current.y === end.y) {
      return current.path;
    }

    // Get available directions
    const directions = getAvailableDirections(
      null,
      current.x,
      current.y,
      visited
    );

    // Randomly decide whether to create a junction (adds extra complexity)
    const shouldCreateJunction = Math.random() < 0.2 && current.path.length > 2;

    // For each available direction, build a new branch of the path.
    for (const dir of directions) {
      visited.add(`${dir.x},${dir.y}`);
      const newPath = [...current.path];

      if (shouldCreateJunction) {
        // Insert a junction pipe (either a T-junction or cross)
        const junctionType =
          Math.random() < 0.5
            ? getRandomItem([
                PIPE_TYPES.T_DOWN,
                PIPE_TYPES.T_LEFT,
                PIPE_TYPES.T_RIGHT,
                PIPE_TYPES.T_UP,
              ])
            : PIPE_TYPES.CROSS;

        newPath.push({
          x: current.x,
          y: current.y,
          type: junctionType,
          hasJunction: true,
        });
      } else if (current.fromDir) {
        // Insert a regular pipe determined by required connections.
        const connections = getRequiredConnections(current.fromDir, dir.dir);
        const pipeType = getPipeTypeForConnections(connections);

        newPath.push({
          x: current.x,
          y: current.y,
          type: pipeType,
          hasJunction: false,
        });
      }

      queue.push({
        x: dir.x,
        y: dir.y,
        path: newPath,
        fromDir: dir.dir,
      });
    }
  }

  return null;
};

// ----------------------
// NEW: Compute rotated connections for a given pipe.
// Returns an array of open directions (e.g., ["top", "right"]) for the given type and rotation.
export const getRotatedConnections = (pipeType, rotation) => {
  const baseConnections = PIPE_CONNECTIONS[pipeType] || [];
  const directionsOrder = ["top", "right", "bottom", "left"];
  const rotationSteps = (rotation / 90) % 4;
  return baseConnections.map((conn) => {
    const index = directionsOrder.indexOf(conn);
    return directionsOrder[(index + rotationSteps) % 4];
  });
};

// ----------------------
// NEW: Leak checker that uses the grid’s connection matrix.
// Iterates over each cell and confirms every connection is either matched by a neighbor or permitted (if an endpoint).
export const checkForLeaks = (grid) => {
  for (let y = 0; y < GRID_SIZE; y++) {
    for (let x = 0; x < GRID_SIZE; x++) {
      const cell = grid[y][x];
      if (cell.type) {
        const cellConnections = getRotatedConnections(cell.type, cell.rotation);
        for (const conn of cellConnections) {
          let nx = x;
          let ny = y;
          if (conn === "top") {
            ny -= 1;
          } else if (conn === "right") {
            nx += 1;
          } else if (conn === "bottom") {
            ny += 1;
          } else if (conn === "left") {
            nx -= 1;
          }
          // Allow off-grid connection only if the cell is an endpoint.
          if (nx < 0 || nx >= GRID_SIZE || ny < 0 || ny >= GRID_SIZE) {
            if (!cell.isEndpoint) {
              return true; // Leak detected.
            }
          } else {
            const neighbor = grid[ny][nx];
            if (!neighbor.type) continue;
            const neighborConnections = getRotatedConnections(
              neighbor.type,
              neighbor.rotation
            );
            let opposite;
            if (conn === "top") {
              opposite = "bottom";
            } else if (conn === "right") {
              opposite = "left";
            } else if (conn === "bottom") {
              opposite = "top";
            } else if (conn === "left") {
              opposite = "right";
            }
            if (!neighborConnections.includes(opposite)) {
              return true; // Leak detected.
            }
          }
        }
      }
    }
  }
  return false;
};

// ----------------------
// Generate a solvable puzzle.
// This function creates an empty grid, sets endpoints, generates a path (with optional junctions),
// assigns random rotations to the path pipes, computes connection matrices, and finally checks for leaks.
// If leaks are found, the puzzle is discarded and generation is retried.
export const generatePuzzle = () => {
  console.log("Starting puzzle generation...");
  const MAX_ATTEMPTS = 10;
  let attempts = 0;

  while (attempts < MAX_ATTEMPTS) {
    console.log(`Attempt ${attempts + 1} of ${MAX_ATTEMPTS}`);

    // Create an empty grid.
    const grid = Array(GRID_SIZE)
      .fill()
      .map(() =>
        Array(GRID_SIZE)
          .fill()
          .map(() => ({
            type: null,
            rotation: 0,
            isEndpoint: false,
            isLocked: false,
            hasJunction: false,
          }))
      );

    // Generate start and end points.
    const start = generateRandomEdgePosition();
    console.log("Generated start position:", start);
    const end = getOppositeEdgePosition(start.x, start.y, start.side);
    console.log("Generated end position:", end);

    if (!end) {
      console.log("Invalid end position, trying again...");
      attempts++;
      continue;
    }

    // Place endpoints.
    grid[start.y][start.x] = {
      type: getEndpointPipeType(start.side),
      rotation: 0,
      isEndpoint: true,
      isLocked: true,
      hasJunction: false,
    };

    grid[end.y][end.x] = {
      type: getEndpointPipeType(end.side),
      rotation: 0,
      isEndpoint: true,
      isLocked: true,
      hasJunction: false,
    };

    // Generate the path between endpoints.
    const path = generatePath(start, end);
    console.log("Generated path:", path);

    if (!path || path.length === 0) {
      console.log("Invalid path, trying again...");
      attempts++;
      continue;
    }

    // Place the path pipes with random rotations.
    path.forEach((pipe) => {
      if (!grid[pipe.y][pipe.x].isEndpoint) {
        grid[pipe.y][pipe.x] = {
          type: pipe.type,
          rotation: getRandomRotation(),
          isEndpoint: false,
          isLocked: false,
          hasJunction: pipe.hasJunction,
        };
      }
    });

    // (Optional) Compute and store connections on each cell.
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        if (grid[y][x].type) {
          grid[y][x].connections = getRotatedConnections(
            grid[y][x].type,
            grid[y][x].rotation
          );
        }
      }
    }

    // Validate the grid by checking for leaks.
    if (checkForLeaks(grid)) {
      console.log("Puzzle has leaks, regenerating puzzle...");
      attempts++;
      continue;
    }

    console.log("Successfully generated puzzle!");
    return grid;
  }

  console.log(
    "Failed to generate valid puzzle after",
    MAX_ATTEMPTS,
    "attempts"
  );
  return null;
};
